import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Button, Badge, PageHeader } from '../../components/ui';
import api from '../../services/api';

const PLAN_NAMES = { pack_one:'Pack One', pack_two:'Pack Two', pack_advanced:'Pack Advanced' };
const STATUS_COLOR = { active:'success', pending_payment:'warning', suspended:'danger', grace_period:'warning', deleted:'danger' };

export default function ClientStats() {
  const { id } = useParams();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStats(); }, [id]);

  const fetchStats = async () => {
    try { const r = await api.get(`/superadmin/clients/${id}/stats`); setData(r.data); }
    catch {} finally { setLoading(false); }
  };

  if (loading) return <div style={{ color:'#555', textAlign:'center', padding:60 }}>Loading...</div>;
  if (!data) return <div style={{ color:'#E74C3C', textAlign:'center', padding:60 }}>Client not found</div>;

  const { client: c, stats: s, campaigns, payments } = data;
  const contactsPct  = c.contactsLimit > 0 ? Math.min(100, (c.contactsUsed / c.contactsLimit) * 100) : 0;
  const sendsPct     = c.emailSendsLimit > 0 ? Math.min(100, (c.emailSendsUsed / c.emailSendsLimit) * 100) : 0;
  const verifyPct    = c.verifyLimit > 0 ? Math.min(100, (c.verifyUsed / c.verifyLimit) * 100) : 0;

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <Link to="/admin/clients" style={{ color:'#555', fontSize:13, textDecoration:'none' }}>← All Clients</Link>
      </div>
      <PageHeader title={c.name} subtitle={`${c.email} · ${c.company||'No company'}`} action={
        <div style={{ display:'flex', gap:8 }}>
          <Link to={`/admin/impersonate/${c.id}`}><Button variant="secondary" size="sm">👁 View as Client</Button></Link>
        </div>
      } />

      {/* Client info */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:20 }}>
        <Card>
          <div style={{ fontSize:12, color:'#2ECC71', fontWeight:800, textTransform:'uppercase', letterSpacing:1, marginBottom:14 }}>Client Details</div>
          {[['Status',<Badge key="s" color={STATUS_COLOR[c.status]||'gray'}>{c.status}</Badge>],['Plan',PLAN_NAMES[c.planId]||c.planId],['Email',c.email],['Company',c.company||'—'],['Phone',c.phone||'—'],['Country',`${c.country||'—'} ${c.state?'· '+c.state:''}`],['GST',c.gstNumber||'Not provided'],['Joined',c.createdAt?new Date(c.createdAt).toLocaleDateString():'-'],['Expires',c.planExpiresAt?new Date(c.planExpiresAt).toLocaleDateString():'—'],['Last Login',c.lastLogin?new Date(c.lastLogin).toLocaleString():'Never']].map(([l,v])=>(
            <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid rgba(255,255,255,0.04)', fontSize:13 }}>
              <span style={{ color:'#555' }}>{l}</span><span style={{ color:'#ccc', fontWeight:500 }}>{v}</span>
            </div>
          ))}
        </Card>

        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {/* Usage stats */}
          {[['📋 Contacts',c.contactsUsed,c.contactsLimit,contactsPct],['📤 Email Sends',c.emailSendsUsed,c.emailSendsLimit,sendsPct],['✅ Verifications',c.verifyUsed,c.verifyLimit,verifyPct]].map(([label,used,limit,pct])=>(
            <Card key={label} style={{ padding:'14px 18px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                <span style={{ fontSize:13, color:'#888' }}>{label}</span>
                <span style={{ fontSize:13, fontWeight:700, color:pct>=90?'#E74C3C':pct>=70?'#F39C12':'#2ECC71' }}>{used?.toLocaleString()} / {limit===-1?'∞':limit?.toLocaleString()}</span>
              </div>
              <div style={{ background:'#1a1a1a', borderRadius:4, height:6 }}>
                <div style={{ width:`${pct}%`, height:'100%', borderRadius:4, background:pct>=90?'#E74C3C':pct>=70?'#F39C12':'#2ECC71', transition:'width 0.5s' }} />
              </div>
            </Card>
          ))}

          {/* Quick stats */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            {[['🎯 Campaigns',s.totalCampaigns],['▶ Active',s.activeCampaigns],['👥 Leads',s.totalLeads],['🔗 Accounts',s.totalAccounts],['📨 Events (30d)',s.events30],['💰 Revenue',`€${s.totalRevenue||0}`]].map(([l,v])=>(
              <div key={l} style={{ background:'#111', border:'1px solid rgba(46,204,113,0.1)', borderRadius:10, padding:'12px 14px', textAlign:'center' }}>
                <div style={{ fontSize:18, fontWeight:800, color:'#2ECC71' }}>{v}</div>
                <div style={{ fontSize:11, color:'#555', marginTop:3 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Campaigns */}
      <Card style={{ marginBottom:20 }}>
        <h3 style={{ fontWeight:700, marginBottom:16 }}>Recent Campaigns</h3>
        {campaigns.length===0 ? <div style={{ color:'#555', fontSize:13 }}>No campaigns yet</div> : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead><tr style={{ borderBottom:'1px solid rgba(46,204,113,0.08)' }}>
                {['Name','Status','Sent','Opened','Clicked','Bounced'].map(h=><th key={h} style={{ padding:'8px 12px', textAlign:'left', color:'#555', fontWeight:700, fontSize:11, textTransform:'uppercase' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {campaigns.map(camp=>(
                  <tr key={camp.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.03)' }}>
                    <td style={{ padding:'9px 12px', color:'#ccc', fontWeight:500 }}>{camp.name}</td>
                    <td style={{ padding:'9px 12px' }}><Badge color={camp.status==='active'?'success':camp.status==='paused'?'warning':'gray'}>{camp.status}</Badge></td>
                    <td style={{ padding:'9px 12px', color:'#888' }}>{camp.statsSent||0}</td>
                    <td style={{ padding:'9px 12px', color:'#3498DB' }}>{camp.statsOpened||0}</td>
                    <td style={{ padding:'9px 12px', color:'#9B59B6' }}>{camp.statsClicked||0}</td>
                    <td style={{ padding:'9px 12px', color:'#E74C3C' }}>{camp.statsBounced||0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Payment history */}
      <Card>
        <h3 style={{ fontWeight:700, marginBottom:16 }}>Payment History</h3>
        {payments.length===0 ? <div style={{ color:'#555', fontSize:13 }}>No payments yet</div> : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {payments.map(p=>(
              <div key={p.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:'#1a1a1a', borderRadius:10 }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color:'#fff' }}>INV-{p.id?.slice(0,8).toUpperCase()}</div>
                  <div style={{ fontSize:11, color:'#555', marginTop:2 }}>{p.createdAt?new Date(p.createdAt).toLocaleDateString():''} · Ref: {p.gatewayOrderId||'—'}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:16, fontWeight:800, color:'#2ECC71' }}>€{p.amount}</div>
                  <Badge color="success" style={{ fontSize:10 }}>{p.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
